/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import React, { useState, useEffect } from "react";
import { useMPSEventRegistration } from "../../../context/MPSEventRegistrationContext";
import {
  formatDateIntoString,
  getDatesBetween,
} from "../../../utils/dateAndTime";
import Question from "../../ui/QuestionComponent";
import { TreePine, Leaf, Check, FileText, Upload, AlertCircle, CheckCircle } from "lucide-react";
import { getVendorDocumentsForEvent, DOCUMENT_TYPE_LABELS } from "../../../services/VendorDocumentService";
import { useAuth } from "../../../context/AuthContext";

const MPSEventRegistration2 = ({
  handleCancel,
  handlePrevious,
  handleEventRegistration,
  eventDetails,
}) => {
  const { user } = useAuth();
  const {
    MPSEventRegistrationData,
    updateMPSEventRegistration,
    getMPSEventRegistrationBusinessInfo,
  } = useMPSEventRegistration();

  // Profile document state
  const [profileDocs, setProfileDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [selectedDocIds, setSelectedDocIds] = useState([]);

  useEffect(() => {
    const loadProfileDocs = async () => {
      if (!user?.uid) return;
      setDocsLoading(true);
      try {
        const result = await getVendorDocumentsForEvent(user.uid);
        if (result.success) setProfileDocs(result.documents);
      } catch (err) {
        console.error('Error loading profile docs:', err);
      } finally {
        setDocsLoading(false);
      }
    };
    loadProfileDocs();
  }, [user?.uid]);


  const availableDates = getDatesBetween(
    eventDetails?.startDate,
    eventDetails?.endDate,
  );
  const [errors, setErrors] = useState({
    datesApplyingFor: "",
  });
  const [formData, setFormData] = useState({
    selectedDates: [],
    extraInfo: "",
    questions: eventDetails?.typeOfQuestionList || [],
    treeDonationOptIn: eventDetails?.treeDonationRequired || false,
  });

  const treeDonationEnabled = eventDetails?.treeDonationEnabled || false;
  const treeDonationRequired = eventDetails?.treeDonationRequired || false;
  const TREE_DONATION_AMOUNT = 2;

  useEffect(() => {
    if (eventDetails?.typeOfQuestionList) {
      setFormData((prevData) => ({
        ...prevData,
        questions: eventDetails.typeOfQuestionList,
      }));
    }
  }, [eventDetails?.typeOfQuestionList]);

  const handleDateChange = (date) => {
    setFormData((prevData) => {
      const updatedDates = prevData.selectedDates.includes(date)
        ? prevData.selectedDates.filter((d) => d !== date)
        : [...prevData.selectedDates, date];

      return {
        ...prevData,
        selectedDates: updatedDates,
        errors: {
          ...prevData.errors,
          datesApplyingFor:
            updatedDates.length === 0 ? "Please select at least one date." : "",
        },
      };
    });
  };

  const handleEventRegistrationClick = async () => {
    if (!validateForm()) return;

    const attachedDocs = profileDocs
      .filter(d => selectedDocIds.includes(d.id))
      .map(d => ({ id: d.id, name: d.name, type: d.type, downloadURL: d.downloadURL, status: d.status }));

    const registrationData = {
      datesApplyingFor: formData.selectedDates,
      additionalUserInfo: formData.extraInfo,
      MPSanswers: formData.questions.filter((q) => q.answer !== ""),
      applicationStatus: "Applied",
      paymentStatus: "Not Paid",
      eventId: eventDetails.id,
      treeDonationOptIn: formData.treeDonationOptIn,
      treeDonationAmount: formData.treeDonationOptIn ? TREE_DONATION_AMOUNT : 0,
      attachedDocuments: attachedDocs,
    };

    const updatedRegistrationData = await updateMPSEventRegistration(
      registrationData,
    );
    handleEventRegistration(updatedRegistrationData);
  };

  const handleQuestionChange = (index, updatedAnswer) => {
    setFormData((prevData) => {
      const updatedQuestions = prevData.questions.map((q, i) =>
        i === index ? { ...q, answer: updatedAnswer } : q,
      );
      return {
        ...prevData,
        questions: updatedQuestions,
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (formData?.selectedDates.length === 0) {
      newErrors.datesApplyingFor = "Please select at least one date.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    validateForm();
  }, [formData?.selectedDates]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFormComplete =
    formData?.selectedDates.length > 0 &&
    formData?.questions
      .filter((q) => Object.keys(q).length > 0)
      .every((q) => q.answer);
  return (
    <div className="flex flex-col w-full h-full gap-6 p-6">
      <div>
        <div className="mb-3 text-left">
          <h2 className="text-lg font-semibold text-grey-900">Dates Applying For</h2>
          <p className="text-sm text-grey-500 mt-1">Which dates are you applying to participate at?</p>
        </div>
        <div className="mx-auto mb-8 space-y-2">
          {availableDates.length > 0 ? (
            availableDates.map((date) => (
              <div key={date} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  onChange={() => handleDateChange(date)}
                  checked={formData?.selectedDates.includes(date)}
                  className="w-4 h-4 rounded border-grey-300 accent-primary-600"
                />
                <label className="text-xs text-grey-900">
                  <span className="text-sm text-grey-900">
                    {formatDateIntoString(date)}
                  </span>
                </label>
              </div>
            ))
          ) : (
            <p className="text-grey-500 text-sm">
              No dates available for this event.
            </p>
          )}
          {errors.datesApplyingFor && (
            <p className="text-error-500 text-sm mt-1">
              {errors.datesApplyingFor}
            </p>
          )}
        </div>

        {/* Additional Questions */}
        <div className="mb-8 space-y-1">
          <div className="mb-4 text-left">
            <h2 className="text-lg font-semibold text-grey-900">Additional Questions</h2>
            <p className="text-sm text-grey-500 mt-1">The event organizer has requested you answer the following questions.</p>
          </div>
          <div>
            {formData.questions.map((question, index) => (
              <Question
                key={index}
                question={question}
                onChange={handleQuestionChange}
                errors={errors}
                index={index}
              />
            ))}
          </div>
        </div>

        {/* Extra Information */}
        <div className="space-y-2 text-base font-medium">
          <label
            htmlFor="description"
            className="block text-sm font-medium text-grey-900"
          >
            Any Extra Information?
          </label>
          <textarea
            id="description"
            rows="4"
            value={formData?.extraInfo || ""}
            onChange={(e) =>
              setFormData((prevData) => ({
                ...prevData,
                extraInfo: e.target.value,
              }))
            }
            className="w-full sm:w-6/12 p-2 border rounded-lg border-grey-300"
            placeholder="Share anything else the organizer should know about your business or setup needs..."
          ></textarea>
        </div>

        {/* Attach Documents from Profile */}
        <div className="mt-8">
          <div className="mb-3 text-left">
            <h2 className="text-lg font-semibold text-grey-900">Attach Documents</h2>
            <p className="text-grey-500 text-sm">
              Select documents from your profile to share with this event organizer.{" "}
              <a href="/mps/settings" target="_blank" rel="noopener noreferrer"
                className="text-primary-600 underline underline-offset-2 hover:text-primary-700">
                Manage profile documents →
              </a>
            </p>
          </div>

          {docsLoading ? (
            <div className="flex items-center gap-2 text-sm text-grey-500 py-4">
              <div className="w-4 h-4 border-2 border-grey-300 border-t-primary-500 rounded-full animate-spin" />
              Loading your documents…
            </div>
          ) : profileDocs.length === 0 ? (
            <div className="flex items-start gap-3 p-4 bg-grey-50 border border-dashed border-grey-300 rounded-xl">
              <FileText className="w-5 h-5 text-grey-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-grey-700">No documents on your profile yet</p>
                <p className="text-xs text-grey-500 mt-0.5">
                  <a href="/mps/settings" target="_blank" rel="noopener noreferrer"
                    className="text-primary-600 underline underline-offset-2">
                    Upload documents in Settings
                  </a>{" "}to attach them to your applications.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {profileDocs.map(doc => {
                const selected = selectedDocIds.includes(doc.id);
                const isExpiringSoon = doc.status === 'expiring_soon';
                return (
                  <label
                    key={doc.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      selected
                        ? 'border-primary-400 bg-primary-50'
                        : 'border-grey-200 bg-white hover:border-grey-300 hover:bg-grey-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() =>
                        setSelectedDocIds(prev =>
                          prev.includes(doc.id)
                            ? prev.filter(id => id !== doc.id)
                            : [...prev, doc.id]
                        )
                      }
                      className="w-4 h-4 rounded border-grey-300 accent-primary-600 flex-shrink-0"
                    />
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      selected ? 'bg-primary-100' : 'bg-grey-100'
                    }`}>
                      <FileText className={`w-4 h-4 ${selected ? 'text-primary-600' : 'text-grey-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-grey-900 truncate">{doc.name}</span>
                        {isExpiringSoon && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded-full flex-shrink-0">
                            <AlertCircle className="w-2.5 h-2.5" /> Expiring soon
                          </span>
                        )}
                        {selected && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-primary-100 text-primary-700 rounded-full flex-shrink-0">
                            <CheckCircle className="w-2.5 h-2.5" /> Selected
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-grey-500 mt-0.5">
                        {DOCUMENT_TYPE_LABELS[doc.type] || doc.type}
                      </p>
                    </div>
                  </label>
                );
              })}
              {selectedDocIds.length > 0 && (
                <p className="text-xs text-primary-600 font-medium pt-1">
                  {selectedDocIds.length} document{selectedDocIds.length !== 1 ? 's' : ''} will be shared with the organizer
                </p>
              )}
            </div>
          )}
        </div>

        {/* Tree Planting Donation Option */}
        {treeDonationEnabled && (
          <div className="mt-8 p-5 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg shrink-0">
                <TreePine className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-grey-900">
                    Plant a Tree for Climate Action
                  </h3>
                  <Leaf className="h-4 w-4 text-green-500" />
                </div>
                <p className="text-sm text-grey-600 mb-4">
                  Add ${TREE_DONATION_AMOUNT} to your registration to plant a
                  tree in a local community through our partner organization.
                </p>

                {treeDonationRequired ? (
                  <div className="flex items-center gap-2 p-3 bg-green-100/50 rounded-lg border border-green-200">
                    <Check className="h-5 w-5 text-green-600" />
                    <div>
                      <span className="text-sm font-medium text-green-800">
                        Tree planting included
                      </span>
                      <p className="text-xs text-green-700 mt-0.5">
                        The event organizer has made this a requirement for all
                        vendors
                      </p>
                    </div>
                    <span className="ml-auto font-semibold text-green-700">
                      +${TREE_DONATION_AMOUNT}
                    </span>
                  </div>
                ) : (
                  <label className="flex items-start gap-3 p-3 bg-white/60 rounded-lg border border-green-100 cursor-pointer hover:bg-white/80 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.treeDonationOptIn}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          treeDonationOptIn: e.target.checked,
                        }))
                      }
                      className="h-5 w-5 mt-0.5 border-grey-300 rounded accent-green-600"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-grey-900">
                        Yes, I want to plant a tree!
                      </span>
                      <p className="text-xs text-grey-500 mt-0.5">
                        100% of your donation goes directly to tree planting
                      </p>
                    </div>
                    <span className="font-semibold text-green-700">
                      +${TREE_DONATION_AMOUNT}
                    </span>
                  </label>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Navigate Buttons */}
        <div className="w-full space-y-6">
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-6 mt-6">
            <button
              onClick={handlePrevious}
              className="px-6 py-3 underline text-grey-900 underline-offset-4"
            >
              Previous
            </button>
            <button
              onClick={handleCancel}
              className="px-6 py-3 border rounded-lg text-grey-900 border-grey-300"
            >
              Cancel Application
            </button>
            <button
              onClick={handleEventRegistrationClick}
              className={`px-6 py-3 text-white rounded-lg ${
                isFormComplete
                  ? "bg-grey-700 hover:bg-grey-800"
                  : "bg-grey-400 cursor-not-allowed"
              }`}
              disabled={!isFormComplete}
            >
              Finish Application
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MPSEventRegistration2;
